"use client";

import { useEffect, useRef, useState } from "react";

/** Куда раскрывать выпадающую панель и какой высоты она может быть.
 *
 *  Раньше панели рельса росли только ВВЕРХ. Пока в них было две строки, это
 *  работало; когда список подрос, верх стал уезжать за экран — заголовок
 *  обрезался, хотя снизу оставалось свободное место.
 *
 *  Теперь по умолчанию раскрываемся ВНИЗ, а наверх уходим только если снизу
 *  не помещается.
 *
 *  Хук замеряет якорь (кнопку, у которой висит панель) и говорит: раскрываться
 *  вниз или вверх и сколько пикселей можно занять. Один и тот же приём нужен и
 *  уведомлениям, и меню профиля — иначе соседние панели ведут себя по-разному,
 *  и это выглядит как поломка.
 */
export function useDropDirection(open: boolean, maxPx = 288, needPx = 240, minPx = 120) {
  const anchorRef = useRef<HTMLElement | null>(null);
  const [dropDown, setDropDown] = useState(true);
  const [maxHeight, setMaxHeight] = useState(maxPx);

  useEffect(() => {
    if (!open) return;
    const measure = () => {
      const r = anchorRef.current?.getBoundingClientRect();
      if (!r) return;
      const GAP = 16;  // запас от края экрана
      const above = Math.round(r.top - GAP);
      const below = Math.round(window.innerHeight - r.bottom - GAP);
      // Вниз — направление по умолчанию: панель раскрывается от кнопки к
      // свободной части экрана и не спорит с краем. Наверх уходим только когда
      // снизу не помещается, а сверху места больше.
      const down = !(below < needPx && above > below);
      setDropDown(down);
      setMaxHeight(Math.max(minPx, Math.min(maxPx, down ? below : above)));
    };
    measure();
    window.addEventListener("resize", measure);
    // Прокрутка страницы двигает якорь — направление пересчитываем и на неё.
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [open, maxPx, needPx, minPx]);

  return { anchorRef, dropDown, maxHeight };
}
